/**
 * The icon set.
 *
 * Every Studio42 application starts with this. Add glyphs an application needs;
 * keep the grid, the stroke weight and `currentColor`.
 *
 * ── Why not emoji ────────────────────────────────────────────────────────────
 *
 * Emoji were quicker and they were the wrong call. They render as a different
 * picture on every platform — 🚗 is a red saloon on Apple, a blue hatchback on
 * Android, and a wireframe on some Windows builds — so a legend that means
 * "this vessel takes cars" is a different drawing to every second person
 * reading it. They cannot inherit `currentColor`, so they ignore the theme and
 * sit at the wrong weight beside real text. Their baseline and advance width
 * are inconsistent, which is exactly what makes a column of them fail to line
 * up. And a screen reader announces the Unicode name, not the meaning.
 *
 * These are one stroke weight, one grid, and `currentColor` throughout, so an
 * icon beside a label is the same colour and weight as the label.
 *
 * STUDIO42 RULE: icons, never emoji, in any application UI. Emoji remain fine
 * in commit messages and documentation.
 */

export type IconName =
  | 'ferry' | 'ship' | 'cargo' | 'car' | 'bicycle' | 'pet' | 'luggage'
  | 'seat' | 'door' | 'accessible' | 'people' | 'person'
  | 'calendar' | 'clock' | 'route' | 'ticket' | 'scan'
  | 'check' | 'alert' | 'info' | 'chevron' | 'back' | 'plus' | 'close'
  | 'chart' | 'settings' | 'key' | 'shield' | 'globe' | 'home' | 'ridge'
  | 'external'

/** 24×24, 1.75 stroke, round caps. One grid so a row of them lines up. */
const PATHS: Record<IconName, string> = {
  // A hull and a wheelhouse. Reads as a boat at 16px, which is the only size
  // that matters here.
  ferry: 'M3 17.5c1.6 0 1.6 1.2 3.2 1.2s1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2 1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2M4.5 14.5 6 9.5h12l1.5 5M8 9.5V6h5l2 3.5M11 6V3.5',
  ship: 'M3 17.5c1.6 0 1.6 1.2 3.2 1.2s1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2 1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2M5 14.5 6.5 10h11L19 14.5M12 10V4M12 4l4 2-4 2',
  // Two peaks and a saddle. The land is a ridge, and at 16px a ridge is the
  // only mountain shape that still reads as one.
  //
  // This is the LOGO. The same string is in studio42.config.json as icon.mark,
  // where scripts/make-icons.mjs rasterises it into the favicon, the home-screen
  // icon and the launcher icons. Change one, change both, or the app in the tab
  // stops matching the app on the page.
  ridge: 'M2.5 18.5h19M4 18.5l5.5-9 3.2 5.2M12.7 14.7 15.5 10l6 8.5M9.5 9.5l1.6-2.6M15.5 10l1.4-2.3',
  cargo: 'M3 8.5h18v10H3zM3 8.5 6 4h12l3 4.5M12 4v4.5M8 12.5h2M14 12.5h2',
  // An arrow leaving an open box. The open corner is the whole message: this
  // one goes somewhere the back button cannot bring you home from.
  external: 'M14 4.5h5.5V10M19.5 4.5 11.5 12.5M17 13.5v5a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 4 18.5v-10A1.5 1.5 0 0 1 5.5 7h5',
  car: 'M4 16.5v2.2M20 16.5v2.2M3 16.5v-4l1.8-4.4A1.6 1.6 0 0 1 6.3 7h11.4a1.6 1.6 0 0 1 1.5 1.1L21 12.5v4zM3 12.5h18M7 14.7h.01M17 14.7h.01',
  bicycle: 'M6.5 18.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M17.5 18.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M6.5 15h5l3.5-7M9.5 8h4M14 8l3.5 7',
  pet: 'M12 20c-2.8 0-4.6-1.7-4.6-3.6 0-1.8 1.7-2.6 2.7-4 .8-1 1-2 1.9-2s1.1 1 1.9 2c1 1.4 2.7 2.2 2.7 4 0 1.9-1.8 3.6-4.6 3.6M6 10.5a1.6 2 30 1 0 0-4 1.6 2 30 1 0 0 4M18 10.5a1.6 2 -30 1 0 0-4 1.6 2 -30 1 0 0 4',
  luggage: 'M5.5 8.5h13v11h-13zM9 8.5V5.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3M8 19.5v1.5M16 19.5v1.5M12 11.5v5',
  seat: 'M7 5.5h2.5a2 2 0 0 1 2 1.8l.6 5.7H7zM5 14.5h14a1.5 1.5 0 0 1 0 3h-1.6l-.8 3H7.4l-.8-3H5a1.5 1.5 0 0 1 0-3M12 13V6.5a1 1 0 0 1 1-1h4',
  // A doorway with the leaf swung open — "by the door", not "an exit sign".
  door: 'M4 20.5h16M7 20.5V5a1 1 0 0 1 1.2-1l7 -1.4A1 1 0 0 1 16.5 3.6v16.9M7 20.5h9.5M13.8 12.2h.01',
  accessible: 'M12 5.2a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8M10 8.2l3.4-.9v3.6l3.1 1.4M10 8.2v4.4h3.4M9.6 12.9a4.2 4.2 0 1 0 5 5.6M13.5 12.6l2 5.9h2.2',
  people: 'M9 11.5a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M2.8 19.8v-.9A4.6 4.6 0 0 1 7.4 14.3h3.2a4.6 4.6 0 0 1 4.6 4.6v.9M16.2 5.4a3.2 3.2 0 0 1 0 6.2M17.6 14.4h.6a4.6 4.6 0 0 1 4.6 4.5v.9',
  person: 'M12 11.8a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2M4.8 20.2v-1a4.8 4.8 0 0 1 4.8-4.8h4.8a4.8 4.8 0 0 1 4.8 4.8v1',
  calendar: 'M4.5 6.5h15v14h-15zM4.5 10.5h15M8.5 3.5v4M15.5 3.5v4M8.5 14h.01M12 14h.01M15.5 14h.01M8.5 17.2h.01M12 17.2h.01',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 7v5.2l3.4 2',
  route: 'M6 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M18 21.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M6 7.5v3.2a3 3 0 0 0 3 3h6a3 3 0 0 1 3 3v.8',
  ticket: 'M3.5 8.2V6.5h17v1.7a2.3 2.3 0 0 0 0 4.6v1.7h-17v-1.7a2.3 2.3 0 0 0 0-4.6M9.5 6.5v8M9.5 8.8v.01M9.5 11.6v.01',
  scan: 'M3.5 8V5a1.5 1.5 0 0 1 1.5-1.5h3M16 3.5h3A1.5 1.5 0 0 1 20.5 5v3M20.5 16v3a1.5 1.5 0 0 1-1.5 1.5h-3M8 20.5H5A1.5 1.5 0 0 1 3.5 19v-3M3.5 12h17',
  check: 'M4.5 12.8 9.5 17.8 19.5 6.8',
  alert: 'M12 8.5v5M12 17h.01M10.3 3.9 2.5 17.4a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 11v5M12 7.6h.01',
  chevron: 'M9 5.5 15.5 12 9 18.5',
  back: 'M15 5.5 8.5 12 15 18.5',
  plus: 'M12 5v14M5 12h14',
  close: 'M6 6l12 12M18 6 6 18',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  settings: 'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M19.1 14.4a1.5 1.5 0 0 0 .3 1.6l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-2.5 1.1v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-2.6-1 1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0-1.1-2.5H4.3a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1-2.6l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.6.3h.1a1.5 1.5 0 0 0 .9-1.4V4.3a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 2.5 1.1l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0 1.1 2.5h.2a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9',
  key: 'M15.5 13.5a5 5 0 1 0-4.6-3H3v3.2h2.6v2.6h2.6v-2.6h2.7a5 5 0 0 0 4.6 2.8M16.8 8.2h.01',
  shield: 'M12 21.5s7.5-3.4 7.5-9.2V5.9L12 2.9 4.5 5.9v6.4c0 5.8 7.5 9.2 7.5 9.2',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M3.2 9.5h17.6M3.2 14.5h17.6M12 3a13.5 13.5 0 0 1 0 18M12 3a13.5 13.5 0 0 0 0 18',
  home: 'M3.5 10.5 12 3.5l8.5 7M5.8 12.4v8.1h12.4v-8.1',
}

export default function Icon({ name, size = 20, className, title }: {
  name: IconName
  size?: number
  className?: string
  title?: string
}) {
  return (
    <svg
      className={['icon', className].filter(Boolean).join(' ')}
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={1.75}
      strokeLinecap="round" strokeLinejoin="round"
      // Decorative unless it is the only thing carrying the meaning.
      role={title ? 'img' : undefined} aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title && <title>{title}</title>}
      <path d={PATHS[name]} />
    </svg>
  )
}

/**
 * Flags, for the language switch.
 *
 * Filled rather than stroked, because a flag read at 20px is a colour, not an
 * outline — and the whole reason a flag beats the word "Español" here is that it
 * is recognised before it is read.
 *
 * Puerto Rico for Spanish rather than Spain: the strings are `es-PR`, the
 * wording is Puerto Rican, and to the people using this on the island the
 * Spanish flag would be somebody else's.
 */
export function Flag({ locale, size = 20 }: { locale: 'en' | 'es-PR'; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', 'aria-hidden': true, focusable: 'false' } as const
  if (locale === 'es-PR') {
    return (
      <svg {...common} className="flag">
        <defs><clipPath id="pr-c"><circle cx="12" cy="12" r="11" /></clipPath></defs>
        <g clipPath="url(#pr-c)">
          <rect x="0" y="1" width="24" height="22" fill="#ED0000" />
          <rect x="0" y="5.4" width="24" height="4.4" fill="#fff" />
          <rect x="0" y="14.2" width="24" height="4.4" fill="#fff" />
          <path d="M0 1 13 12 0 23Z" fill="#0050F0" />
          <path d="m4.3 9.4.86 2.02 2.19.17-1.67 1.43.51 2.14-1.89-1.15-1.89 1.15.51-2.14L1.25 11.6l2.19-.17Z" fill="#fff" />
        </g>
        <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeOpacity=".18" />
      </svg>
    )
  }
  return (
    <svg {...common} className="flag">
      <defs><clipPath id="us-c"><circle cx="12" cy="12" r="11" /></clipPath></defs>
      <g clipPath="url(#us-c)">
        <rect x="0" y="1" width="24" height="22" fill="#fff" />
        {[0, 2, 4, 6, 8, 10].map((i) => (
          <rect key={i} x="0" y={1 + i * 1.7 + 1.7} width="24" height="1.7" fill="#B22234" />
        ))}
        <rect x="0" y="1" width="24" height="1.7" fill="#B22234" />
        <rect x="0" y="1" width="10.5" height="11.9" fill="#3C3B6E" />
        {[[2.4, 3.2], [5.2, 3.2], [8, 3.2], [3.8, 5.4], [6.6, 5.4], [2.4, 7.6], [5.2, 7.6], [8, 7.6], [3.8, 9.8], [6.6, 9.8]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r=".82" fill="#fff" />
        ))}
      </g>
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeOpacity=".18" />
    </svg>
  )
}
